import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Sparkles, Gem, Heart, Gift } from "lucide-react";
import { MaskedLines, Reveal, RevealWords } from "@/components/site/Reveal";
import { AmazonButton, ProductCard } from "@/components/site/ProductCard";
import { EditorialMarquee, SlimMarquee } from "@/components/site/Marquee";
import { PRODUCTS, IMAGES, AMAZON_STORE, INSTAGRAM_URL } from "@/data/site";

const easing = [0.22, 1, 0.36, 1];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative w-full h-[94svh] min-h-[600px] overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Fashiella luxury hair accessories"
          className="h-[114%] w-full object-cover object-center"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#4a352f]/70 via-[#4a352f]/28 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#4a352f]/45 via-transparent to-[#4a352f]/10" />

      <div className="relative z-10 mx-auto max-w-7xl w-full h-full px-6 sm:px-10 flex flex-col justify-center">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 1 }}
          className="text-xs uppercase tracking-[0.4em] text-[#E8C7A8] mb-5"
        >
          Handmade Luxury Hair Accessories
        </motion.p>
        <h1
          className="font-display font-medium text-[40px] md:text-[64px] leading-[1.12] tracking-[0.5px] text-[#E8C7A8]"
          style={{ textShadow: "0 1px 12px rgba(45,28,24,0.5)" }}
        >
          <MaskedLines lines={["Created With Love."]} delay={0.3} lineClass="pt-[0.04em] pb-[0.22em]" />
          <MaskedLines lines={["Designed For"]} delay={0.44} lineClass="pt-[0.04em] pb-[0.22em]" />
          <MaskedLines lines={["Elegance."]} delay={0.56} lineClass="pt-[0.04em] pb-[0.22em]" />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9, ease: easing }}
          className="mt-7 max-w-md text-[#FFF8F0]/90 leading-relaxed text-lg font-sans"
          style={{ textShadow: "0 1px 8px rgba(45,28,24,0.4)" }}
        >
          Beautifully crafted hair accessories inspired by timeless moments and everyday elegance.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9, ease: easing }}
          className="mt-9 flex flex-wrap gap-3"
        >
          <Link
            to="/catalogue"
            data-testid="hero-explore-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-[#FFF8F0] text-charcoal px-8 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors duration-400 hover:bg-[#E8C7A8]"
          >
            Explore Catalogue
            <ArrowUpRight size={15} className="transition-transform duration-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <AmazonButton href={AMAZON_STORE} label="Shop on Amazon" variant="ghostLight" testid="hero-amazon-btn" />
        </motion.div>
      </div>

      <div className="absolute bottom-7 left-6 sm:left-10 z-10 hidden lg:flex items-center gap-3 text-[#FFF8F0]/60">
        <span className="h-px w-10 bg-[#FFF8F0]/40" />
        <span className="text-[0.6rem] uppercase tracking-[0.3em]">Scroll to discover</span>
      </div>
    </section>
  );
};

const Intro = () => (
  <section className="mx-auto max-w-7xl px-5 sm:px-8 py-28 lg:py-40" data-testid="intro-section">
    <div className="grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-rosegold mb-6">The Fashiella Feeling</p>
        </Reveal>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
          <RevealWords text="Elegance In" />
          <br />
          <span className="italic text-rosegold"><RevealWords text="Every Detail" delay={0.1} /></span>
        </h2>
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-md text-charcoal/60 leading-relaxed text-lg">
            At Fashiella, we believe the smallest details create the most beautiful impressions. Each accessory
            is designed to bring effortless elegance, confidence and charm to every look.
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-6">
        <Reveal delay={0.1}>
          <div className="relative overflow-hidden aspect-[5/6]">
            <img src={IMAGES.intro} alt="Elegant styling" className="h-full w-full object-cover" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const chapters = [
  {
    n: "01",
    title: "A Mother's Love",
    body: "Fashiella began with a mother's love and a dream to create something beautiful for her daughter.",
  },
  {
    n: "02",
    title: "A Beautiful Beginning",
    body: "Inspired by the happiness of styling hair, creating special moments and celebrating individuality, Fashiella was born to bring elegant accessories that make every wearer feel special.",
  },
  {
    n: "03",
    title: "Thoughtful By Design",
    body: "Each piece reflects thoughtful design, beautiful craftsmanship and the belief that even the smallest details can create unforgettable moments.",
  },
];

const StorySection = () => (
  <section className="bg-blush/40 py-28 lg:py-40" data-testid="story-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-rosegold mb-6">Our Story</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            A Mother's Love, <span className="italic text-rosegold">A Beautiful Beginning</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-sm text-charcoal/60 leading-relaxed text-lg">
            Every accessory represents love, creativity, and attention to detail — a quiet celebration of the little moments that make us feel beautiful.
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-center">
        {chapters.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.08}>
            <div className="py-8 border-b border-black/10 flex gap-7">
              <span className="font-serif text-5xl text-rosegold/70 leading-none">{c.n}</span>
              <div>
                <h3 className="font-serif text-2xl mb-2">{c.title}</h3>
                <p className="text-charcoal/60 leading-relaxed">{c.body}</p>
              </div>
            </div>
          </Reveal>
        ))}
        <Reveal delay={0.1}>
          <Link to="/our-story" data-testid="story-readmore-link" className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] group">
            Read The Full Story
            <ArrowUpRight size={15} className="transition-transform duration-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const Catalogue = () => (
  <section className="bg-champagne/40 py-28 lg:py-40" data-testid="catalogue-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.3em] text-rosegold mb-5">The Catalogue</p>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-2xl">
          Pieces made to be <span className="italic text-rosegold">treasured</span>.
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-x-7 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
        {PRODUCTS.slice(0, 9).map((p, i) => (
          <Reveal key={p.id} delay={(i % 3) * 0.07}>
            <ProductCard product={p} index={i} />
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <div className="mt-16 text-center">
          <Link to="/catalogue" data-testid="catalogue-viewall-link" className="group inline-flex items-center gap-2 rounded-full border border-charcoal px-8 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors duration-400 hover:bg-charcoal hover:text-ivory">
            View Full Catalogue
            <ArrowUpRight size={15} className="transition-transform duration-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const reasons = [
  { icon: Sparkles, title: "Thoughtful Designs", body: "Every piece is imagined with intention, care and a love for detail." },
  { icon: Gem, title: "Premium Materials", body: "Satin, velvet, organza and pearls chosen to feel as good as they look." },
  { icon: Heart, title: "Elegant Details", body: "Delicate finishes that turn an everyday moment into something special." },
  { icon: Gift, title: "Perfect Gifts", body: "Beautifully considered pieces made to be gifted and treasured." },
];

const WhyFashiella = () => (
  <section className="bg-blush/40 py-28 lg:py-40" data-testid="why-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-16 max-w-xl">Why Fashiella</h2>
      </Reveal>
      <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
        {reasons.map((r, i) => {
          const Icon = r.icon;
          return (
            <Reveal key={r.title} delay={i * 0.08}>
              <div className="border-t border-charcoal/15 pt-7" data-testid={`why-card-${i}`}>
                <Icon size={26} strokeWidth={1.4} className="text-rosegold mb-6" />
                <h3 className="font-serif text-2xl mb-3">{r.title}</h3>
                <p className="text-charcoal/60 leading-relaxed text-[0.95rem]">{r.body}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const AmazonCTA = () => (
  <section className="mx-auto max-w-7xl px-5 sm:px-8 py-28 lg:py-40" data-testid="amazon-cta-section">
    <Reveal>
      <div className="relative overflow-hidden bg-nude/60 px-6 sm:px-16 py-24 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-rosegold mb-6">Fashiella on Amazon</p>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl mx-auto leading-[1.05]">
          Discover Your Perfect <span className="italic text-rosegold">Fashiella</span> Piece
        </h2>
        <p className="mt-7 max-w-lg mx-auto text-charcoal/60 leading-relaxed text-lg">
          Explore our complete collection available on Amazon.
        </p>
        <div className="mt-10 flex justify-center">
          <AmazonButton href={AMAZON_STORE} label="Visit Amazon Store" testid="cta-amazon-btn" />
        </div>
      </div>
    </Reveal>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <EditorialMarquee items={["Elegance In Every Detail", "Created With Love", "Fashiella"]} />
      <Intro />
      <StorySection />
      <Catalogue />
      <WhyFashiella />
      <AmazonCTA />
      <SlimMarquee items={["Follow Us On Instagram", "@shopfashiella", "Style Your Moment"]} href={INSTAGRAM_URL} />
    </>
  );
}
